/**
 * Cross-subdomain Persistenz fuer die UI-Sprache.
 *
 * Schreibt sowohl ein Cookie `amzel-lang` auf `.amzel.io` (damit
 * nest.amzel.io und app.amzel.io die Wahl teilen) als auch
 * localStorage("amzel.lang") fuer lokale Dev-Setups ohne `.amzel.io`-Host.
 *
 * Reihenfolge beim Lesen: Cookie > localStorage > Default "en".
 *
 * Die exakt gleiche Logik liegt in `frontend/src/i18n/storage.ts` —
 * Aenderungen bitte synchron halten.
 */

export const SUPPORTED_LANGS = ["en", "de", "pl", "fr", "it"] as const;
export type LangCode = (typeof SUPPORTED_LANGS)[number];

const COOKIE_NAME = "amzel-lang";
const STORAGE_KEY = "amzel.lang";

function isSupported(code: string | null | undefined): code is LangCode {
  return code !== null && code !== undefined && (SUPPORTED_LANGS as readonly string[]).includes(code);
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
}

export function readStoredLang(): LangCode {
  if (typeof window === "undefined") return "en";
  const fromCookie = readCookie(COOKIE_NAME);
  if (isSupported(fromCookie)) return fromCookie;
  const fromStorage = window.localStorage.getItem(STORAGE_KEY);
  if (isSupported(fromStorage)) return fromStorage;
  return "en";
}

export function writeStoredLang(lang: string): void {
  if (typeof window === "undefined") return;
  if (!isSupported(lang)) return;
  window.localStorage.setItem(STORAGE_KEY, lang);
  const oneYearSeconds = 60 * 60 * 24 * 365;
  const isSecure = window.location.protocol === "https:";
  const secureFlag = isSecure ? "; Secure" : "";
  const host = window.location.hostname;
  const domainAttr = host.endsWith("amzel.io") ? "; domain=.amzel.io" : "";
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(lang)}; path=/; max-age=${oneYearSeconds}; SameSite=Lax${secureFlag}${domainAttr}`;
}
